import React, { cloneElement, Fragment } from 'react';
import PropTypes from 'prop-types';
import { some } from 'lodash';


import { buildClassNames, buildClassNameModifiers } from '../util/utils';
import { Divider } from '../divider';
import './list.scss';

/* Block */
const blockBaseClassName = 'list';

/* Elements */
const headerClassName = 'list__header';
const listClassName = 'list__list';
const itemClassName = 'list__item';

/* Modifiers */
const cardModifier = 'card';
const scrollModifier = 'scroll';
const noBorderModifier = 'no-border';

/** A component for displaying multiple items as a continuous element */
export const List = ({
                children,
                className,
                header,
                maxHeight,
                noBorder,
                withDividers,
                ...other
              }) => {
  const blockClassNames = buildClassNames(
    className,
    buildClassNameModifiers(blockBaseClassName, !maxHeight && noBorder && noBorderModifier),
    blockBaseClassName,
  );
  const hasCards = some(React.Children.toArray(children), child => child.props
    && child.props.isCard);
  const listClassNames = buildClassNames(
    buildClassNameModifiers(listClassName, hasCards && cardModifier, maxHeight && scrollModifier),
    listClassName,
  );
  const heightStyles = { maxHeight: (maxHeight && `${maxHeight}px`) || 'auto' };
  const childrenCount = withDividers ? React.Children.count(children) : undefined;
  return (
    <div role='list' className={blockClassNames} {...other}>
      {header && <div className={headerClassName}>{header}</div>}
      <div className={listClassNames} style={heightStyles}>
        {React.Children.map(
          children,
          (child, index) => (
            <Fragment key={child.key || `listitem-${index}`}>
              {cloneElement(
                child,
                {
                  role: 'listitem',
                  className: buildClassNames(child.props.className, itemClassName),
                },
              )}
              {withDividers && (index !== childrenCount - 1) && <Divider />}
            </Fragment>
          ),
        )}
      </div>
    </div>
  );
};

List.propTypes = {
  /** @ignore */
  children: PropTypes.node,
  /** @ignore */
  className: PropTypes.string,
  /** Text to be displayed in the header of the list */
  header: PropTypes.node,
  /** Maximum height for the content in the list, with scrolling if contents are too large */
  maxHeight: PropTypes.number,
  /** Indicates whether the list will be rendered without a border.
   * NOTE: ignored if a header is specified) */
  noBorder: PropTypes.bool,
  /** Determines whether the list items will be rendered with dividers in between */
  withDividers: PropTypes.bool,
};

List.defaultProps = { noBorder: false };

export default List;
